import { CountryBoundType, type Country, type FoundedCountry } from '@/shared/types/api.types'
import type { Locale } from '@/shared/types/common.types'
import HttpService from '../HttpService'
import countriesHash from './countries'
import type { CountryResponse } from './CountriesService.types'
import { useCacheService } from '@/shared/composables/useCacheService'

const getPopularity = (boundType: CountryBoundType, response: CountryResponse): number => {
  let value
  if (boundType === CountryBoundType.OUTBUND) {
    value = response.popularOutbound
  } else {
    value = response.popularInbound
  }

  return value || 999
}

export class CountriesService extends HttpService {
  async getCountries(
    boundType: CountryBoundType = CountryBoundType.OUTBUND,
    locale?: string,
    name?: string
  ): Promise<Country[]> {
    const CACHE_KEY = `countries_${boundType}_${locale ?? 'en'}`
    const localStorageCacheService = useCacheService()
    const cachedData = localStorageCacheService.get(CACHE_KEY)

    if (cachedData && !name) {
      return cachedData
    }

    const productCode = boundType === CountryBoundType.OUTBUND ? 'traveloutbound' : 'travelinbound'
    const response = await this.get<CountryResponse[]>('/listcountries', {
      params: {
        productCode,
        locale,
        name,
      },
    })

    if (response.length) {
      // TODO-DS: After full migration to QC-connect, need create one composables for sorting
      const countries = response
        .map((el) => {
          const popularity = getPopularity(boundType, el)

          return {
            id: el.countryCode,
            name: el.countryName,
            popular: String(popularity),
            isocode: el.iso?.toLowerCase() ?? (countriesHash[el.countryCode] || ''),
            code: el.countryCode,
            searchString: el.searchWords,
          }
        })
        .sort((countryA, countryB) => Number(countryB.popular) - Number(countryA.popular))

      localStorageCacheService.set(CACHE_KEY, countries, { hours: 12 })

      return countries
    }

    return []
  }

  findCountries(
    boundType: CountryBoundType = CountryBoundType.OUTBUND,
    locale?: Locale,
    name?: string
  ): Promise<FoundedCountry[]> {
    return this.getCountries(boundType, locale, name).then((res) =>
      res.map((el) => ({
        code: el.code,
        desc: el.name,
        isocode: el.isocode,
      }))
    )
  }
}
